/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Theme } from '@emotion/react'
import { Breakpoints, Components, ThemeOptions, DrawerProps } from '@mui/material'
import { TypographyOptions } from '@mui/material/styles/createTypography'
import navBackground from '../assets/images/nav-bg.png'
import { DRAWER_DIMENSIONS } from '../util/consts'
import { TypographyComponentTheme } from './theme'

export default function components(
  theme: ThemeOptions & {
    breakpoints: Breakpoints
    typography: TypographyOptions & TypographyComponentTheme
  },
) {
  const components: Components<Omit<Theme, 'components'>> = {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          ...theme.typography.MuiTypography?.defaultProps?.variantMapping,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          height: '100%',
          overflow: 'scroll',
          margin: 'inherit',
        },
      },
    },
    MuiRadio: {
      variants: [
        {
          props: { color: 'tertiary' },
          style: {
            color: theme.colors.shade0,
          },
        },
        {
          props: { color: 'subtle' },
          style: {
            color: theme.colors.primary100,
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '12px 24px',
          borderRadius: '32px',
          textTransform: 'none',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: theme.colors.primary800,
          },
          '&:active': {
            backgroundColor: theme.colors.primary900,
          },
          '&:disabled': {
            backgroundColor: theme.colors.grey300,
            color: theme.colors.grey700,
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: theme.colors.secondary300,
          },
          '&:active': {
            backgroundColor: theme.colors.secondary400,
          },
          '&:disabled': {
            backgroundColor: theme.colors.grey300,
            color: theme.colors.grey700,
          },
        },
        outlinedPrimary: {
          border: '1px solid',
          '&:hover': {
            backgroundColor: theme.colors.primary100,
          },
          '&:active': {
            backgroundColor: theme.colors.primary200,
          },
          '&:disabled': {
            backgroundColor: theme.colors.grey300,
            color: theme.colors.grey700,
          },
        },
        textPrimary: {
          fontWeight: 600,
          textDecoration: 'underline',
          textDecorationColor: theme.colors.primary500,
          '&:hover': {
            textDecoration: 'none',
            backgroundColor: theme.colors.primary50,
          },
          '&:active': {
            backgroundColor: theme.colors.primary100,
          },
          '&:disabled': {
            backgroundColor: theme.colors.grey300,
            color: theme.colors.grey700,
            textDecorationColor: theme.colors.grey700,
          },
        },
      },
      variants: [
        {
          props: { variant: 'underline', color: 'primary' },

          style: {
            color: theme.colors.primary500,
            padding: 0,
            textDecoration: 'underline',
            ...theme.typography.link,
            '&:hover': {
              textDecoration: 'none',
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'underline', color: 'greyed' },

          style: {
            color: theme.palette?.greyed.dark,
            textDecoration: 'underline',
            textDecorationThickness: '1px',
            textUnderlineOffset: '4px',
            '&:hover': {
              textDecoration: 'none',
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'tertiary' },
          style: {
            color: theme.colors.secondary800,
          },
        },

        {
          props: { variant: 'contained', color: 'subtle' },
          style: {
            color: theme.colors.primary700,
            '&:hover': {
              backgroundColor: theme.colors.primary200,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'tertiary' },
          style: {},
        },
      ],
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 16px 16px 16px',
          gap: '12px',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: 0,
        },
        subheader: {
          order: 1,
          color: theme.colors.secondary700,
        },
        title: {
          order: 2,
          '& a': {
            flexDirection: 'row',
            display: 'flex',
            alignItems: 'center',
            width: 'fit-content',
            ...theme.typography.h3,
            color: theme.colors.primary500,
            textDecoration: 'none',
            padding: '4px 8px',
            marginLeft: '-8px',
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: theme.colors.primary50,
            },
            '& .material-symbols-outlined': {
              paddingLeft: '8px',
              fontWeight: 400,
              margin: 'auto 0 0',
              paddingBottom: '3px',
            },
          },
        },
        action: {
          padding: '12px 0 0 0',
          '& .MuiLink-root': {
            ...theme.typography.h6,
            marginRight: '4px',
            fontWeight: 700,
            color: 'inherit',
            textDecorationColor: 'inherit',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: theme.colors.primary500,
          color: theme.colors.shade0,
          boxShadow: 'none',
        },
      },
      variants: [
        {
          props: {
            variant: undefined,
          },
          style: {
            '& *': {
              color: theme.colors.shade0,
              backgroundColor: 'unset',
            },
            '& .MuiTypography-root': {
              color: 'inherit',
            },
            '& .MuiCardHeader-root': {
              '& a': {
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'unset',
                  span: {
                    marginBottom: '-1px',
                    borderBottom: `1px solid ${theme.colors.shade0}`,
                  },
                },
              },
            },
          },
        },
        {
          props: {
            variant: 'outlined',
          },
          style: {
            '&.MuiPaper-outlined': {
              border: `1px solid ${theme.colors.primary100}`,
              backgroundColor: theme.colors.shade0,
              color: theme.colors.primary500,
            },
          },
        },
      ],
    },

    MuiPaper: {
      styleOverrides: {
        elevation1: {
          borderRadius: 0,
        },
      },
    },

    MuiDrawer: {
      variants: [
        {
          props: {
            'farmlands-variant': 'desktop',
          } as DrawerProps,
          style: {
            zIndex: 1100,
            position: 'fixed',

            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              backgroundColor: theme.colors.primary500,
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              top: '0',
              height: `calc(var(--doc-height) - ${DRAWER_DIMENSIONS.margin.vertical.lg * 2}px)`,
              width: '20vw',
              maxWidth: DRAWER_DIMENSIONS.width.lg,
              minWidth: DRAWER_DIMENSIONS.width.sm,
              margin: `${DRAWER_DIMENSIONS.margin.vertical.lg}px ${DRAWER_DIMENSIONS.margin.horizontal.lg}px`,
              [theme.breakpoints.between('md', 'lg')]: {
                height: `calc(var(--doc-height) - ${DRAWER_DIMENSIONS.margin.vertical.md * 2}px)`,
                margin: `${DRAWER_DIMENSIONS.margin.vertical.md}px ${DRAWER_DIMENSIONS.margin.horizontal.md}px`,
              },
            },
          },
        },

        {
          props: {
            'farmlands-variant': 'mobile',
            anchor: 'left',
            elevation: 0,
            hideBackdrop: true,
          } as DrawerProps,
          style: {
            zIndex: 1100,
            height: 'calc(var(--doc-height) - 80px)',

            '& .MuiDrawer-paper': {
              width: '100vw',
              height: 'calc(var(--doc-height) - 80px)',
              backgroundColor: theme.colors.primary50,

              '& not(.MuiBadge-badge)': {
                color: theme.colors.primary500,
              },
            },
          },
        },
      ],
    },
    MuiList: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          rowGap: '16px',
          width: '100%',
          '& a.active': {
            backgroundColor: theme.colors.primary700,
            borderRadius: '4px',
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        role: 'menu',
      },
      styleOverrides: {
        list: {
          rowGap: '0',
          padding: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,

          '& .Mui-disabled': {
            backgroundColor: theme.colors.grey50,
            fontWeight: theme.colors.grey600,
            borderRadius: '0',
          },
          '& em': {
            fontStyle: 'normal',
          },
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        role: 'menuitem',
      },
      styleOverrides: {
        root: {
          padding: '8px 16px',
          ...theme.typography.body2,
          borderRadius: 0,
          color: theme.colors.primary500,
          '&.Mui-selected': {
            backgroundColor: theme.colors.primary50,
            borderRadius: '0',
            borderLeft: `2px solid ${theme.colors.primary500} !important`,
          },

          '&:hover, :focus, .Mui-focused': {
            backgroundColor: theme.colors.primary100,
          },
          '& .MuiListItemIcon-root': {
            minWidth: 24,
          },
          '& .MuiListItemText-root': {
            '& .MuiTypography-root': {
              [theme.breakpoints.down('md')]: {
                ...theme.typography.caption,
              },
            },
          },
        },
      },
      variants: [
        {
          props: { 'farmlands-variant': 'dark' } as any,
          style: {
            borderRadius: '4px',
            '&:hover, :focus, .Mui-focused': {
              backgroundColor: theme.colors.primary800,
            },
            '&:active': {
              backgroundColor: theme.colors.primary700,
            },
            '& .MuiListItemIcon-root': {
              color: theme.colors.primary100,
            },
            '& .MuiListItemText-root': {
              '& .MuiTypography-root': {
                color: theme.colors.primary100,
              },
            },
            textTranform: 'none',
          },
        },
        {
          props: { 'farmlands-variant': 'light' } as any,
          style: {
            // marginLeft: '-16px',
            padding: '16px 16px',
            '& .MuiListItemIcon-root': {
              color: theme.colors.primary500,
              marginRight: '16px',
            },
            '& .MuiListItemText-root': {
              '& .MuiTypography-root': {
                color: theme.colors.primary500,
              },
            },

            '&:hover, :focus, .Mui-focused': {
              backgroundColor: theme.colors.primary100,
            },
            '&:active': {
              backgroundColor: theme.colors.primary100,
            },
            textTranform: 'none',
          },
        },
      ],
    },

    MuiListItemText: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            ...theme.typography.body2,
            color: theme.colors.primary500,
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          width: '100%',
          '& .MuiFormControl-root': {
            width: '100%',
          },
          '& .MuiInputAdornment-root': {
            marginRight: '-5px',
          },
        },
        inputRoot: {
          paddingTop: '10.5px',
          paddingBottom: '10.5px',
          paddingLeft: '16px',
        },
        input: {
          padding: '0px !important',
          width: '100%',
          '& input': {
            textOverflow: 'ellipsis',
            ...theme.typography.body2,
            '&::placeholder': {
              color: theme.colors.grey700,
              opacity: 1,
            },
          },
        },
        listbox: {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          rowGap: '0',
          padding: 0,

          '& .Mui-disabled': {
            backgroundColor: theme.colors.primary50,
            borderRadius: '0',
          },
          '& em': {
            fontStyle: 'normal',
          },
          '& .MuiAutocomplete-option': {
            padding: '8px 16px',
            '&.Mui-focused': {
              backgroundColor: theme.colors.primary100,
            },
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme.colors.primary50,
            '&.Mui-focused': {
              backgroundColor: theme.colors.primary100,
            },
          },
        },
        option: {
          ...theme.typography.body2,
          color: theme.colors.primary500,
          ' &.Mui-focusVisible': {
            backgroundColor: theme.colors.primary100,
          },
          ' &:focus': {
            backgroundColor: theme.colors.primary100,
          },
          '&.Mui-focused': {
            backgroundColor: theme.colors.primary50,
          },
          '&:active': {
            backgroundColor: theme.colors.primary50,
          },
          '&:hover': {
            backgroundColor: theme.colors.primary100,
          },
        },
        popupIndicator: {
          color: theme.colors.primary500,
        },
        endAdornment: {
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        },
      },
    },

    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          zIndex: 9999,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px',
          padding: '0px 16px',
          backgroundColor: theme.colors.primary500,
          backgroundImage: `url(${navBackground})`,

          [theme.breakpoints.up('md')]: {
            display: 'none',
          },
        },
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: theme.colors.primary100,
          '& .material-symbols-outlined': {
            fontWeight: 200,
          },
          '&.Mui-selected': {
            color: theme.colors.shade0,
            fontWeight: 700,
            '& .material-symbols-outlined': {
              fontVariationSettings: '"FILL" 1, "wght" 300, "GRAD" 0, "opsz" 48',
            },
          },
        },
        label: {
          fontSize: '14px',
          lineHeight: '21px',
          letterSpacing: '0.02em',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: '100%',
          borderRadius: '8px',
          padding: '10.5px 16px',
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: theme.colors.shade0,
        },
        input: {
          ...theme.typography.body2,
          height: '24px',
          padding: '0px !important',
          textOverflow: 'ellipsis',
          '&::placeholder': {
            color: theme.colors.grey700,
            opacity: 1,
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        root: {
          width: '100%',
          '& .MuiFormControl-root': {
            width: '100%',
          },
          '& .MuiInputAdornment-root': {
            width: '24px',
            height: '24px',
          },
        },
        input: {
          padding: '0px !important',
          width: '100%',
          '& input': {
            textOverflow: 'ellipsis',
            ...theme.typography.body2,
            '&::placeholder': {
              color: theme.colors.grey700,
              opacity: 1,
            },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '10.5px 28px 10.5px 16px!important',
        },
        icon: {
          height: '24px',
          top: 'calc(50% - .7em)',
          '& .material-symbols-outlined': {
            color: theme.colors.primary500,
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...theme.typography.body2,
          fontWeight: 700,
          margin: '0 0 4px 0',
          '&.Mui-focused': {
            color: theme.colors.grey800,
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...theme.typography.body2,
          color: 'inherit',
          margin: '4px 0 0 0',
        },
      },
    },

    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: theme.colors.primary500,
          marginLeft: 0,
          '& .MuiIconButton-root': {
            '&:hover': {
              background: theme.colors.primary100,
            },
            '&:focus-visible': {
              background: theme.colors.primary100,
              border: `1px solid ${theme.colors.primary500}`,
              borderRadius: `${45 / 2}px`,
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        filled: {
          ...theme.typography.caption,
          color: theme.colors.primary500,
          background: theme.colors.primary100,
          '&:hover': {
            background: theme.colors.primary200,
            color: theme.colors.primary900,
          },
        },
      },
      variants: [
        {
          props: {
            color: 'info',
          },
          style: {
            color: theme.colors.grey900,
            background: theme.colors.grey200,
            height: '22px',
            fontSize: '14px',
            '& .MuiChip-label': {
              padding: '0px 16px',
              lineHeight: '100%',
            },
            padding: '0px',
            '&:hover': {
              background: theme.colors.grey200,
              color: theme.colors.grey900,
            },
          },
        },
        {
          props: {
            color: 'secondary',
          },
          style: {
            color: theme.colors.shade0,
            background: theme.colors.secondary700,
            height: '22px',
            fontSize: '14px',
            '& .MuiChip-label': {
              padding: '0px 8px',
              lineHeight: '100%',
            },
            padding: '0px',
            '&:hover': {
              color: theme.colors.shade0,
              background: theme.colors.secondary700,
            },
          },
        },
      ],
    },

    MuiPagination: {
      styleOverrides: {
        root: {
          marginTop: '32px',
          [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
          },
        },
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          ...theme.typography.body2,
          height: '44px',
          width: '44px',
          borderRadius: '22px',
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          ...theme.typography.h3,
        },
        indicator: {
          height: '3px',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          ...theme.typography.h3,
          fontWeight: 400,
          fontSize: 'inherit',
          textTransform: 'none',
          padding: '14px 0',
          minWidth: '70px',
          '&.Mui-selected': {
            fontWeight: 600,
            '&:hover': {
              boxShadow: `0 -3px 0 0 ${theme.colors.primary500} inset`,
            },
          },
          '&:hover': {
            color: theme.colors.primary800,
            boxShadow: `0 -3px 0 0 ${theme.colors.primary300} inset`,
          },
          [theme.breakpoints.down('sm')]: {
            ...theme.typography.h5,
            fontWeight: 400,
            textTransform: 'none',
            padding: '8px 0',
          },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            verticalAlign: 'top',
            '&:last-child td, &:last-child th': { border: 0 },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: theme.colors.primary100,
        },
        head: {
          ...theme.typography.body2,
          fontWeight: 600,
          padding: 0,
        },
        body: {
          ...theme.typography.body2,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          verticalAlign: 'top',
        },
      },
    },

    MuiModal: {
      styleOverrides: {
        root: {
          zIndex: 9999,
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paperFullScreen: {
          '@media (max-width: 600px)': {
            paddingRight: '0px',
            '& > div': {
              paddingRight: '16px',
            },
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 0,
          ...theme.typography.h2,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        dividers: {},
        root: {
          // display: 'grid',
          // position: 'relative',
          // left: 0,
          // right: 100,
          // top: 0,
          // bottom: 0,
          // padding: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          margin: 0,
          color: theme.colors.primary500,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          zIndex: 999,
        },
      },
    },
  }

  return components
}
